import React, { useState, useEffect } from 'react';
import {
  keys,
  omit,
  zipObject,
  stubTrue,
  xor,
  cond,
  constant,
  get,
} from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, MiniLoader } from '@pwc/pwc-deals-ui';

import './MultiSelectSearch.scss';

const applyButtonId = 'apply-tag-button';
const createButtonId = 'create-tag-button';
const optId = (_strings, opt) => `opt-${opt.id}`;

const useBlurHandler = (ids, onFocusOutside) => (ev) => {
  const { relatedTarget } = ev;
  const focusedId = get(relatedTarget, 'id', '');
  const focusIsWithin = ids.some((id) => focusedId === id);

  if (relatedTarget && !focusIsWithin && onFocusOutside) {
    onFocusOutside();
  }
};

const shouldShowApplyButton = ({
  searchCriteria,
  exactMatch,
  tagsChanged,
  allowCreateTags,
}) => {
  if (!allowCreateTags) {
    return tagsChanged;
  }

  return tagsChanged || (searchCriteria && exactMatch);
};

const getApplyButton =
  (
    onSubmit,
    currentlySelectedTags,
    allowCreateTags,
    blurHandler = () => {},
    handleEscapeKey = () => {},
    t
  ) =>
  ({ tagsChanged }) =>
    (
      <div className="multi-select-action-button-container">
        <Button
          id={applyButtonId}
          data-testid={applyButtonId}
          onClick={() => onSubmit(keys(currentlySelectedTags))}
          onBlur={blurHandler}
          type="button"
          disabled={!tagsChanged}
          onKeyDown={handleEscapeKey}
        >
          {allowCreateTags ? t('btnApply') : t('filterContent')}
        </Button>
      </div>
    );

const shouldShowCreateButton = ({
  searchCriteria,
  exactMatch,
  tagsChanged,
  allowCreateTags,
}) => {
  if (!allowCreateTags) {
    return false;
  }

  return searchCriteria && !exactMatch && !tagsChanged;
};

const getCreateButton =
  (
    onSubmit,
    currentlySelectedTags,
    blurHandler = () => {},
    handleEscapeKey = () => {},
    t
  ) =>
  ({ searchCriteria }) =>
    (
      <div className="multi-select-action-button-container">
        <Button
          id={createButtonId}
          data-testid={createButtonId}
          onClick={() =>
            onSubmit([...keys(currentlySelectedTags), searchCriteria])
          }
          onBlur={blurHandler}
          type="button"
          onKeyDown={handleEscapeKey}
        >
          {t('btn.create')}
        </Button>
      </div>
    );

const insensitive = (str) => {
  return str.trim().toLowerCase();
};

const MultiSelectSearch = ({
  searchCriteria,
  options,
  onSubmit,
  selectedTags,
  allowCreateTags,
  closeDropdown,
  innerIdWhitelist = [],
  isLoading,
}) => {
  const [originalSelectedTags, setOrignalSelectedTags] = useState([]);
  const [currentlySelectedTags, setCurrentlySelectedTags] = useState({});
  const [viewableOptions, setViewableOptions] = useState([]);
  const [tagsChanged, setTagsChanged] = useState(false);
  const [exactMatch, setExactMatchChanged] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const optionsMap = options.reduce(
      (acc, option) => ({ ...acc, [option.text]: option }),
      {}
    );

    setOrignalSelectedTags(selectedTags.filter((tag) => optionsMap[tag]));
  }, [selectedTags, options]);

  useEffect(() => {
    setCurrentlySelectedTags(
      zipObject(originalSelectedTags, originalSelectedTags.map(stubTrue))
    );
  }, [originalSelectedTags]);

  useEffect(() => {
    setTagsChanged(
      xor(keys(currentlySelectedTags), originalSelectedTags).length
    );
  }, [currentlySelectedTags, originalSelectedTags]);

  useEffect(() => {
    const searchCriteriaInsensitive = insensitive(searchCriteria);
    const insensitiveOptionsText = options.map((option) =>
      insensitive(option.text)
    );
    const insensitiveSelectedTags = selectedTags.map(insensitive);

    setViewableOptions(
      options.filter((option) => {
        const optionText = insensitive(option.text);
        return optionText.startsWith(searchCriteriaInsensitive);
      })
    );

    setExactMatchChanged(
      !![...insensitiveOptionsText, ...insensitiveSelectedTags].find(
        (str) => str === searchCriteriaInsensitive
      )
    );
  }, [searchCriteria, options, selectedTags]);

  const updateCheckedTags = (selectedTag) => {
    const alreadyChecked = currentlySelectedTags[selectedTag];
    if (alreadyChecked) {
      const updatedTags = omit(currentlySelectedTags, [selectedTag]);

      setCurrentlySelectedTags(updatedTags);
    } else {
      setCurrentlySelectedTags({
        ...currentlySelectedTags,
        [selectedTag]: true,
      });
    }
  };
  const blurHandler = useBlurHandler(
    [
      ...options.map((opt) => optId`${opt}`),
      ...innerIdWhitelist,
      applyButtonId,
      createButtonId,
    ],
    closeDropdown
  );

  const handleEscapeKey = (ev) => {
    if (ev.keyCode === 27) {
      closeDropdown();
    }
  };

  const actions = cond([
    [
      shouldShowApplyButton,
      getApplyButton(
        onSubmit,
        currentlySelectedTags,
        allowCreateTags,
        blurHandler,
        handleEscapeKey,
        t
      ),
    ],
    [
      shouldShowCreateButton,
      getCreateButton(
        onSubmit,
        currentlySelectedTags,
        blurHandler,
        handleEscapeKey,
        t
      ),
    ],
    [stubTrue, constant(null)],
  ])({ tagsChanged, searchCriteria, exactMatch, allowCreateTags });

  if (isLoading === true) {
    return (
      <div
        className="multi-select-container loading"
        data-testid="multi-select-container"
      >
        <MiniLoader />
      </div>
    );
  }

  return (
    <div
      className="multi-select-container"
      data-testid="multi-select-container"
    >
      {viewableOptions.length > 0 && (
        <div className="scrollable">
          {viewableOptions.map((option) => (
            <Checkbox
              key={option.text}
              label={option.displayText}
              id={optId`${option}`}
              dataTestId={option.text}
              checked={currentlySelectedTags[option.text] === true}
              onChange={() => {
                updateCheckedTags(option.text);
              }}
              onBlur={blurHandler}
              onKeyDown={handleEscapeKey}
            />
          ))}
        </div>
      )}
      {actions}
    </div>
  );
};

export default MultiSelectSearch;
